import React from 'react';
import { Card, Tag, Typography, Tooltip } from 'antd';
import { Database, AlertTriangle, CheckCircle } from 'lucide-react';
import { Handle, Position, NodeProps } from 'reactflow';

const { Text } = Typography;

interface SQLQueryNodeData {
  label: string;
  description?: string;
  dataSourceId: string;
  dataSourceName?: string;
  query: string;
  outputFormat?: 'json' | 'table' | 'csv' | 'count';
  resultLimit?: number;
  timeout?: number;
  isExecuting?: boolean;
  isCompleted?: boolean;
  hasError?: boolean;
  onDelete?: () => void;
  isUnlinked?: boolean;
}

const SQLQueryNode: React.FC<NodeProps<SQLQueryNodeData>> = ({ 
  data, 
  selected 
}) => {
  const {
    label = 'SQL Query',
    description,
    dataSourceId,
    dataSourceName,
    query,
    outputFormat = 'json',
    resultLimit,
    isExecuting = false,
    isCompleted = false,
    hasError = false,
    isUnlinked = false
  } = data;

  const getNodeStyle = (): React.CSSProperties => {
    let baseStyle: React.CSSProperties = {
      minWidth: '200px',
      maxWidth: '280px',
      border: '2px solid',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      background: '#ffffff'
    };

    if (selected) {
      baseStyle.borderColor = '#1890ff';
      baseStyle.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
    } else if (isExecuting) {
      baseStyle.borderColor = '#faad14';
      baseStyle.boxShadow = '0 0 8px rgba(250, 173, 20, 0.3)';
    } else if (hasError) {
      baseStyle.borderColor = '#ff4d4f';
    } else if (isCompleted) {
      baseStyle.borderColor = '#52c41a';
    } else if (isUnlinked) {
      baseStyle.borderColor = '#ff4d4f';
      baseStyle.borderStyle = 'dashed';
    } else {
      baseStyle.borderColor = '#52c41a';
    }

    return baseStyle;
  };

  const getStatusIcon = () => {
    if (isExecuting) {
      return <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full" />;
    } else if (hasError) {
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    } else if (isCompleted) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return null;
  };

  const truncateQuery = (query: string, maxLength: number = 50) => {
    if (!query) return 'No query specified';
    if (query.length <= maxLength) return query;
    return query.substring(0, maxLength) + '...';
  };

  return (
    <div style={getNodeStyle()}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: '#52c41a',
          border: '2px solid #fff',
          width: '12px',
          height: '12px'
        }}
      />
      
      <Card size="small" variant="borderless" style={{ border: 'none' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Database className="w-5 h-5 text-green-600" />
            <Text strong style={{ fontSize: '14px' }}>{label}</Text>
          </div>
          {getStatusIcon()}
        </div>

        {/* Description */}
        {description && (
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
            {description}
          </Text>
        )}

        {/* Data Source */}
        {dataSourceId && (
          <div style={{ marginBottom: '8px' }}>
            <Tag 
              color={dataSourceName ? 'green' : 'orange'}
              style={{ fontSize: '11px' }}
            >
              {dataSourceName || `Source: ${dataSourceId.slice(0, 8)}...`}
            </Tag>
          </div>
        )}

        {/* Query Preview */}
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '6px 8px', 
          borderRadius: '4px',
          marginBottom: '8px',
          fontSize: '11px',
          fontFamily: 'monospace',
          color: '#666'
        }}>
          <Tooltip title={query} placement="bottom">
            {truncateQuery(query)}
          </Tooltip>
        </div>

        {/* Configuration Tags */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <Tag color="blue" style={{ fontSize: '10px' }}>
            {outputFormat?.toUpperCase() || 'JSON'}
          </Tag>
          {resultLimit && (
            <Tag color="purple" style={{ fontSize: '10px' }}>
              Limit: {resultLimit}
            </Tag>
          )}
          {isUnlinked && (
            <Tag color="red" style={{ fontSize: '10px' }}>
              Unlinked
            </Tag>
          )}
        </div>

        {/* Execution Status */}
        {isExecuting && (
          <div style={{ 
            marginTop: '8px', 
            padding: '4px 8px', 
            backgroundColor: '#fff7e6',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#fa8c16'
          }}>
            Executing query...
          </div>
        )}

        {hasError && (
          <div style={{ 
            marginTop: '8px', 
            padding: '4px 8px', 
            backgroundColor: '#fff2f0',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#ff4d4f'
          }}>
            Execution failed
          </div>
        )}
      </Card>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: '#52c41a',
          border: '2px solid #fff',
          width: '12px',
          height: '12px'
        }}
      />
    </div>
  );
};

export default SQLQueryNode;